import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Spinner, Divider, StackView, Spacer } from '@telus-uds/components-web';

import request from '../../../utils/request';
import { useLocaleContext } from '../../../context/LocaleContext';
import { ErrorMessage } from '../css';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';

// Matches the styled file-input pattern in ../../fieldFile.js — hides the native
// input (default browser "Choose File" chrome looks out of place next to UDS
// components) and drives it via a styled, clickable label instead.
const ChooseFileButton = styled.label`
  display: inline-block;
  margin: 0;
  padding: 0.25em 0.75em;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  font-family: inherit;
  text-align: center;
  color: #333;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 2px;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    background-color: #e8e8e8;
    border-color: #b3b3b3;
  }

  &:focus {
    outline: 1px dotted #333;
    outline-offset: 2px;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
`;

const FileUpload = ({ addFileToList, orderId, fieldName, maximumFileSize }) => {
  const locale = useLocaleContext();
  const {
    general: {
      chooseFile,
      fileSelectError,
      fileUploadingError,
      fileSizeError,
      uploading,
      uploadLabel
    }
  } = locale === 'en-US' ? EN : FR;
  const [selectedFile, setSelectedFile] = useState();
  const [showError, setShowError] = useState({ isVisible: false, message: '' });
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef();

  const validateFileSize = file => {
    if (!maximumFileSize) return true;
    const fileSize = file.size / 1024 / 1024;
    if (fileSize > maximumFileSize) {
      setShowError({
        isVisible: true,
        message: fileSizeError.replace('**SIZE**', maximumFileSize)
      });
      inputRef.current.value = '';
      return false;
    }
    return true;
  };

  const onFileSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) return;
    setShowError({ isVisible: false, message: '' });
    setSelectedFile(file);
  };

  const uploadFile = () => {
    const formData = new FormData();
    formData.append(fieldName, selectedFile);
    formData.append('typeId', 0);
    return request.post(`/orders/${orderId}/upload-file`, formData);
  };

  const onFileUpload = async () => {
    if (!selectedFile) {
      setShowError({
        isVisible: true,
        message: fileSelectError
      });
      return;
    }
    setIsUploading(true);
    try {
      const result = await uploadFile();
      if (result) {
        const {
          data: { orderFiles }
        } = result;
        addFileToList(orderFiles);
        setIsUploading(false);
        setSelectedFile(null);
        inputRef.current.value = '';
      }
    } catch (error) {
      setShowError({
        isVisible: true,
        message: fileUploadingError
      });
      setIsUploading(false);
      throw error;
    }
  };

  return (
    <>
      <Divider />
      <Spacer space={4} />
      <StackView space={2}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <HiddenFileInput
              id={`file-upload-${fieldName}`}
              type="file"
              name={fieldName}
              onChange={onFileSelect}
              ref={inputRef}
            />
            <ChooseFileButton htmlFor={`file-upload-${fieldName}`}>
              {chooseFile}
            </ChooseFileButton>
            {selectedFile && (
              <span className="frg-body-font-14px">{selectedFile.name}</span>
            )}
          </div>
        </div>
        <Spacer space={4} />
        <div>
          <Spinner label={uploading} variant={{ size: 'small' }} show={isUploading} inline>
            <Button onClick={onFileUpload}>{uploadLabel}</Button>
          </Spinner>
          {showError.isVisible && (
            <ErrorMessage>{showError.message}</ErrorMessage>
          )}
        </div>
      </StackView>
    </>
  );
};

FileUpload.propTypes = {
  addFileToList: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fieldName: PropTypes.string.isRequired,
  maximumFileSize: PropTypes.number
};

FileUpload.defaultProps = {
  maximumFileSize: null
};

export default FileUpload;
